import React, { useEffect, useCallback, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
  Typography,
  Box,
} from "@mui/material";
import * as services from "./../../../services/saleng";
import { formatNumber } from "../../../utils/formatNumber";

const PriceSelectionDialog = ({
  open,
  onClose,
  onSelect,
  categoryId,
  productPrice,
}) => {
  const [prices, setPrices] = useState([]);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPriceId, setSelectedPriceId] = useState(null);

  const fetchCategories = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await services.getCategories();
      const categoryData = res.data.data || [];

      const matchedCategory = categoryData.find((cat) => cat.id === categoryId);
      if (matchedCategory) {
        matchedCategory.prices = (matchedCategory.prices ?? []).map(
          (price) => ({
            ...price,
            adjPrice: (productPrice?.basePrice ?? 0) + (price?.adjPrice ?? 0),
          })
        );
        matchedCategory.adjPrice = productPrice?.basePrice ?? 0;
      }

      setData(matchedCategory);

      // รวม basePrice เข้าไปในราคาที่เลือกได้ (สร้าง obj ชื่อ id = 'base' เพื่อไม่ให้ชนกับ id จริง)
      const combinedPrices = [
        {
          id: "base", // id ที่ไม่ซ้ำกับ ids จริง
          minWeight: 0,
          adjPrice: productPrice?.basePrice ?? 0,
          label: "ราคาพื้นฐาน",
        },
        ...(matchedCategory?.prices ?? []),
        productPrice.groupName && {
          id: "groupName",
          minWeight: 0,
          adjPrice:
            productPrice?.basePrice + productPrice?.groupAdjustment ?? 0,
          label: "ราคากลุ่ม",
        },
      ];

      setPrices(combinedPrices);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
      setPrices([]);
    } finally {
      setIsLoading(false);
    }
  }, [categoryId, productPrice]);

  useEffect(() => {
    if (open) {
      fetchCategories();
      setSelectedPriceId(null); // รีเซ็ตตอนเปิด dialog
    }
  }, [fetchCategories, open]);

  useEffect(() => {
    if (open && prices.length > 0) {
      if (
        productPrice?.adjPrice !== undefined &&
        productPrice?.adjPrice !== null
      ) {
        // หา index ของตัวสุดท้ายที่ตรงเงื่อนไข
        const lastIndex = prices
          .map((p) => p?.adjPrice)
          .lastIndexOf(productPrice?.adjPrice);

        if (lastIndex !== -1) {
          setSelectedPriceId(prices[lastIndex].id.toString());
        } else {
          setSelectedPriceId(null);
        }
      } else {
        setSelectedPriceId(null);
      }
    }
  }, [open, prices, productPrice]);

  const handleChange = (event) => {
    setSelectedPriceId(event.target.value);
  };

  const handleConfirm = () => {
    const selectedPrice = prices.find(
      (p) => p.id.toString() === selectedPriceId
    );
    if (selectedPrice) {
      onSelect(selectedPrice);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>เลือกราคาสินค้า</DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <CircularProgress />
        ) : prices.length === 0 ? (
          <Typography>ไม่มีราคาสินค้าให้เลือก</Typography>
        ) : (
          <>
            {/* Header */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              px={2}
              py={1}
              sx={{ color: "text.secondary" }}
            >
              <Typography>น้ำหนักขั้นต่ำ (กก.)</Typography>
              <Typography>ราคา (บาท/กก.)</Typography>
            </Box>
            <RadioGroup value={selectedPriceId} onChange={handleChange}>
              {prices
                .filter((price) => price && price.id != null)
                .map((price) => (
                  <FormControlLabel
                    key={price.id}
                    value={price.id.toString()}
                    control={<Radio />}
                    disableTypography
                    label={
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ flex: 1 }}
                      >
                        <Typography>
                          <Typography>
                            {price.label !== "ราคากลุ่ม"
                              ? `${price.minWeight ?? 0} `
                              : ""}
                            {price.label === "ราคาพื้นฐาน"
                              ? ""
                              : price.label === "ราคากลุ่ม"
                                ? `กลุ่ม ${productPrice?.groupName ?? ""}`
                                : `(หมวด ${data?.name ?? ""})`}
                          </Typography>
                        </Typography>
                        <Typography>
                          {formatNumber(price?.adjPrice ?? 0)}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
            </RadioGroup>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>ยกเลิก</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!selectedPriceId}
        >
          ตกลง
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PriceSelectionDialog;
